import * as React from "react";
import { Dispatch } from "redux";
import { connect } from "react-redux";
import { DraggableConsumer } from "../draggable/DraggableContext";
import { GanttChartSource } from "../reducers/gantt/properties";

import type { DraggableProviderContext } from "../draggable/DraggableContext";
import type { TranslatePoint, DraggingOrder } from "../draggable/reducers";
import type { HighlightShiftLineAction } from "../reducers/planningTable";
import type { ReduxState } from "../reducers/reducers";

import img_cursor from "@/img/cursor.svg";

type ActionTypes = HighlightShiftLineAction;

export type OwnProps = {
    translate_cursor?: TranslatePoint | null;
    dragging_order?: DraggingOrder | null;
    children?: React.ReactNode;
};

type Props = OwnProps & ReduxProps;

type State = {
    visible: boolean;
};

class Cursor extends React.Component<Props, State> {
    state: State = {
        visible: false,
    };

    node = React.createRef<SVGImageElement>();

    getTransform() {
        if (this.props.translate_cursor) {
            const translate_y = this.props.translate_cursor.translate_y;
            let translate_x = this.props.translate_cursor.translate_x;
            if (this.props.properties.shift_width) {
                translate_x = translate_x - this.props.properties.shift_width / 2;
            }
            return `translate(${translate_x}, ${translate_y})`;
        }
        return "";
    }

    getIsPlanningTable = () => {
        return (
            this.props.properties.source_type === GanttChartSource.planning_table ||
            this.props.properties.source_type === GanttChartSource.planning_table_parallel
        );
    };

    render() {
        if (!this.getIsPlanningTable()) {
            return null;
        }

        if ((this.props.dragging_order && !this.props.dragging_order.order_uuid) || !this.props.dragging_order) {
            return null;
        }

        const width = this.props.properties.shift_width;
        const height = this.props.properties.bar_height;

        return (
            <image
                ref={this.node}
                id="cursor"
                transform={this.getTransform()}
                style={{ pointerEvents: "none" }}
                xlinkHref={img_cursor}
                x={0}
                y={0}
                width={width ?? undefined}
                height={height}
                opacity={this.props.translate_cursor !== null ? 1 : 0}
            />
        );
    }
}

const CursorContext = (props: Props) => {
    return (
        <DraggableConsumer>
            {(value: DraggableProviderContext | null) => {
                if (!value) {
                    return null;
                }

                return (
                    <Cursor
                        translate_cursor={value.state.translate_cursor}
                        dragging_order={value.state.dragging_order}
                        {...props}
                    >
                        {props.children}
                    </Cursor>
                );
            }}
        </DraggableConsumer>
    );
};

const mapState = (state: ReduxState) => {
    return {
        properties: state.gantt_chart_properties,
    };
};

const mapDispatch = (dispatch: Dispatch<ActionTypes>) => ({ reduxDispatch: dispatch });

type ReduxProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

export default connect<ReturnType<typeof mapState>, ReturnType<typeof mapDispatch>, OwnProps, ReduxState>(
    mapState,
    mapDispatch
)(CursorContext);
